import { z } from 'zod';
import { normalizeUrl } from './format.js';
import { isSafeHttpUrl } from './doctemplate.js';
import { MAX_ASK_REASON } from './profile/asks.js';
import { KEY_RE } from './profile/key-rule.js';
// ── Intake forms: agent-customizable, talent-facing animated walkthrough ────
// One field per screen; heading/info fields render as interstitial cards.
export const INTAKE_FIELD_TYPES = [
    'short_text',
    'long_text',
    'email',
    'phone',
    'date',
    'number',
    'select',
    'multi_select',
    'matrix', // grid: one choice (column) per row
    'file',
    'info', // interstitial card: title + body, no answer
    'link', // document link everyone assigned the form opens/signs (DocuSign/DocHub/etc.), no answer
];
export const INTAKE_FIELD_TYPE_LABELS = {
    short_text: 'Short answer',
    long_text: 'Long answer',
    email: 'Email',
    phone: 'Phone',
    date: 'Date',
    number: 'Number',
    select: 'Choose one',
    multi_select: 'Choose any',
    matrix: 'Grid (one per row)',
    file: 'File upload',
    info: 'Info card',
    link: 'Document link',
};
/**
 * Field types that render as a display-only card and collect NO answer: an `info` heading and a
 * `link` (a document everyone signs). One predicate so every runner, validator, and question-count
 * filter treats them identically. When adding another display-only type, add it here, not inline.
 */
export const DISPLAY_ONLY_FIELD_TYPES = ['info', 'link'];
export const isDisplayOnlyField = (t) => DISPLAY_ONLY_FIELD_TYPES.includes(t);
const RESERVED_FIELD_IDS = new Set(['__proto__', 'constructor', 'prototype']);
/** THE field id rule: a short slug that can never shadow an object's own properties. Every id (a form
 *  field, a library question's legacy id, a projected key's id) is checked by this one schema. */
export const fieldIdSchema = z
    .string()
    .regex(/^[a-z0-9_]{1,48}$/, 'field id must be a short slug')
    .refine((id) => !RESERVED_FIELD_IDS.has(id), 'reserved field id');
export const isFieldId = (id) => fieldIdSchema.safeParse(id).success;
/**
 * How many choices one field offers (a list's options, a grid's rows), by who wrote it:
 * - what an AGENCY types into a form or its profile schema (intakeFieldSchema, the form editor and the schema save):
 *   at most 30 options and 40 rows, as always;
 * - what is STORED (storedIntakeFieldSchema: every read of a stored form or schema, profileFieldsOf) and what the
 *   question library gives an agency (spec 3.1 ensureSchemaField): at most MAX_FIELD_CHOICES. The library holds lists
 *   this long (countries, languages, heritages: up to 286; a grid of 227 accents), so its own schema
 *   (profile/types.ts libraryQuestionSchema) shares this bound: every library question is a valid stored field.
 * Each choice stays at most 120 characters and a grid at most 12 columns.
 */
export const MAX_INPUT_OPTIONS = 30;
export const MAX_INPUT_ROWS = 40;
export const MAX_FIELD_CHOICES = 300;
/** The most fields an agency's client profile schema holds (businesses.profile_fields, the settings save). */
export const MAX_PROFILE_FIELDS = 60;
const choice = z.string().trim().min(1).max(120);
/** One field, its choice lists bounded as `bounds` says (see above). */
const fieldSchemaWith = (bounds) => z.object({
    id: fieldIdSchema,
    type: z.enum(INTAKE_FIELD_TYPES),
    label: z.string().trim().min(1).max(200),
    help: z.string().max(1000).optional(),
    required: z.boolean().optional(),
    /** select / multi_select choices. */
    options: z.array(choice).max(bounds.options).optional(),
    /** matrix rows (e.g. divisions): one answer collected per row. */
    rows: z.array(choice).max(bounds.rows).optional(),
    /** matrix columns: the choices offered for every row. */
    columns: z.array(choice).max(12).optional(),
    /** file fields: what to accept. */
    accept: z.enum(['image', 'document', 'any']).optional(),
    /** link fields: the external URL the talent opens (http/https). Display-only, collects no answer. */
    url: z.string().trim().max(2000).optional(),
    /**
     * Profile linkage (intake ↔ client profile sync):
     *   true  → answers write to the client profile; the field joins the business
     *           profile schema if it isn't there yet.
     *   false → form-only; the answer stays on the submission.
     *   unset → writes to the profile iff the id already exists in the schema.
     */
    profile: z.boolean().optional(),
    /**
     * Who can change this profile field in the talent portal. The client can always SEE every
     * field they answered (they have a right to see what they provided/agreed to):
     *   'client_read' → shown in the portal, read-only.
     *   'client_edit' → the client may update it (default).
     * 'internal' is DEPRECATED (kept only so older stored forms still validate) and is treated
     * everywhere as 'client_read': nothing is hidden from the client anymore.
     */
    access: z.enum(['internal', 'client_read', 'client_edit']).optional(),
    /**
     * Sensitive PII (SSN-class): encrypted at rest (AES-256-GCM, app key), value never
     * echoed to the portal, and the WHOLE TEAM needs an MFA-stepped-up session (aal2)
     * to read or write it. Access of either kind is audit-logged.
     */
    sensitive: z.boolean().optional(),
    /**
     * A library form item (one profile, spec 3.20, Phase 6): the question it asks, the way it asks it (a code way
     * `<key>~<n>` or `<key>~label`, a stored way `v:<id>`, or `f:<id>` for the field itself) and the agency's reason
     * for asking. The item is still a COMPLETE field (type, label, help, choices), so every reader of raw fields works
     * without the library; `resolveFormFields` only refreshes its wording for display. A legacy field has none.
     */
    question_key: z.string().regex(KEY_RE).optional(),
    variant_key: z.string().trim().min(1).max(200).optional(),
    reason: z.string().trim().max(MAX_ASK_REASON).optional(),
});
/** A field as an agency writes it (a form save, the profile schema save): the agency input bound. */
export const intakeFieldSchema = fieldSchemaWith({ options: MAX_INPUT_OPTIONS, rows: MAX_INPUT_ROWS });
/** A field as it may be stored and read back (and as the question library gives it): the stored bound. */
export const storedIntakeFieldSchema = fieldSchemaWith({ options: MAX_FIELD_CHOICES, rows: MAX_FIELD_CHOICES });
/**
 * A stored field list read field by field (a form's fields, a profile schema): every field the stored bound accepts
 * (one bad field never hides the rest), and whether all of them passed. Anything but a list reads as none.
 */
export function storedFieldsOf(stored) {
    if (!Array.isArray(stored))
        return { fields: [], complete: true };
    const fields = [];
    let complete = true;
    for (const f of stored) {
        const parsed = storedIntakeFieldSchema.safeParse(f);
        if (parsed.success)
            fields.push(parsed.data);
        else
            complete = false;
    }
    return { fields, complete };
}
/**
 * An agency's save of a field list (a form's fields, its profile schema) with the fields it keeps exactly as they are
 * stored: `raw` with every such field that only the STORED bound holds (a library question the agency asks, given it
 * by ensureSchemaField, then saved back or copied into a form) replaced by a stand-in the input bound holds, so the
 * save validates by the agency input bound (intakeFieldSchema) with its own messages; `restore` puts the stored
 * fields back into the parsed list. With no such field (every save before a library field exists), `raw` is returned
 * as it came and the save validates exactly as before. `asGiven` (Phase 6): whether a field is a library item exactly
 * as its way gives it (a new item from the picker, with the agency's own settings), kept the same way: the library's
 * lists are bounded by the stored bound, and anything the agency changed is still held to the input bound.
 */
export function keepingStoredFields(raw, kept, asGiven) {
    if (!Array.isArray(raw) || (!kept.length && !asGiven))
        return { list: raw, restore: (fields) => fields };
    // Both sides are storedIntakeFieldSchema outputs, so their JSON (keys in schema order) is canonical.
    const keptById = new Map(kept.map((f) => [f.id, JSON.stringify(f)]));
    const swapped = new Map();
    const list = raw.map((f, i) => {
        if (intakeFieldSchema.safeParse(f).success)
            return f;
        const stored = storedIntakeFieldSchema.safeParse(f);
        if (!stored.success || (keptById.get(stored.data.id) !== JSON.stringify(stored.data) && !asGiven?.(stored.data)))
            return f;
        swapped.set(i, stored.data);
        return { id: stored.data.id, type: 'short_text', label: stored.data.label };
    });
    if (!swapped.size)
        return { list: raw, restore: (fields) => fields };
    return { list, restore: (fields) => fields.map((f, i) => swapped.get(i) ?? f) };
}
export const intakeFormInputSchema = z.object({
    title: z.string().trim().min(1).max(160),
    intro: z.string().max(4000).optional().nullable(),
    outro: z.string().max(4000).optional().nullable(),
    fields: z.array(intakeFieldSchema).min(1).max(60),
    status: z.enum(['active', 'paused']).optional(),
});
/** An uploaded-file answer (set by the runner after a signed upload). */
export const intakeFileAnswerSchema = z.object({
    bucket: z.string(),
    path: z.string().max(600),
    file_name: z.string().max(300),
    mime_type: z.string().max(120).optional().nullable(),
    size_bytes: z.number().int().nonnegative().optional().nullable(),
});
export const intakeAnswerValueSchema = z.union([
    z.string().max(4000),
    z.array(z.string().max(300)).max(MAX_FIELD_CHOICES), // multi_select: as many picks as a field has choices
    intakeFileAnswerSchema,
    z.record(z.string(), z.string().max(120)), // matrix: row label → chosen column
]);
/** Validate a submission's answers against the form's fields. Returns cleaned answers. */
export function validateIntakeAnswers(fields, answers) {
    // Null prototype: a field id like __proto__ can never rewrite the accumulator's prototype.
    const out = Object.create(null);
    for (const f of fields) {
        if (isDisplayOnlyField(f.type))
            continue;
        const raw = answers[f.id];
        const empty = raw == null ||
            (typeof raw === 'string' && !raw.trim()) ||
            (Array.isArray(raw) && raw.length === 0);
        if (empty) {
            if (f.required)
                return { ok: false, error: `"${f.label}" is required` };
            continue;
        }
        if (f.type === 'file') {
            const parsed = intakeFileAnswerSchema.safeParse(raw);
            if (!parsed.success)
                return { ok: false, error: `"${f.label}" has an invalid file` };
            out[f.id] = parsed.data;
            continue;
        }
        if (f.type === 'multi_select') {
            if (!Array.isArray(raw))
                return { ok: false, error: `"${f.label}" has an invalid answer` };
            // As many picks as a field can offer choices (every one of a long library list), never cut short: a longer
            // list is refused, so an answer is never stored as less than the talent picked.
            const vals = raw.map(String);
            if (vals.length > MAX_FIELD_CHOICES)
                return { ok: false, error: `"${f.label}" has too many choices` };
            if (f.options && !vals.every((v) => f.options.includes(v)))
                return { ok: false, error: `"${f.label}" has an unknown choice` };
            out[f.id] = vals;
            continue;
        }
        if (f.type === 'matrix') {
            if (typeof raw !== 'object' || raw === null || Array.isArray(raw))
                return { ok: false, error: `"${f.label}" has an invalid answer` };
            const rows = f.rows ?? [];
            const cols = f.columns ?? [];
            // Null prototype again — row ids come straight from the submission.
            const grid = Object.create(null);
            for (const [rowKey, colVal] of Object.entries(raw)) {
                if (!rows.includes(rowKey))
                    continue; // drop unknown rows
                const c = String(colVal);
                if (cols.length && !cols.includes(c))
                    continue; // drop unknown columns
                grid[rowKey] = c;
            }
            if (f.required && Object.keys(grid).length === 0)
                return { ok: false, error: `"${f.label}" is required` };
            out[f.id] = grid;
            continue;
        }
        const s = String(raw).slice(0, 4000);
        if (f.type === 'email' && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(s))
            return { ok: false, error: `"${f.label}" must be a valid email` };
        if (f.type === 'select' && f.options && !f.options.includes(s))
            return { ok: false, error: `"${f.label}" has an unknown choice` };
        out[f.id] = s;
    }
    return { ok: true, answers: out };
}
// ── Onboarding document links ───────────────────────────────────────────────
// External "complete this" links an agency hands a talent when inviting them:
// DocuSign / DocHub / Zenbooks contracts, e-signature packets, questionnaires.
// They ride on the invite and land on the talent's intake-form assignment, where
// they surface as a "Documents to complete" section in the talent's portal intake.
/** How many document links one talent's intake may carry. */
export const MAX_ONBOARDING_LINKS = 20;
/** One external document/action link (label + http(s) url + an optional note). */
export const intakeLinkSchema = z.object({
    label: z.string().trim().min(1).max(120),
    url: z.string().trim().min(1).max(2000),
    note: z.string().trim().max(400).optional(),
});
/**
 * Normalize + validate a raw links array (agent-supplied). A bare host gains `https://`; any
 * link that is not http(s) after normalization is DROPPED (blocks `javascript:`/`data:`/`mailto:`
 * (these are outbound `<a href>`s the talent clicks). Blank/invalid rows are skipped and the
 * result is capped at MAX_ONBOARDING_LINKS. Pure and total: never throws, safe on the wire.
 */
export function cleanIntakeLinks(raw) {
    if (!Array.isArray(raw))
        return [];
    const out = [];
    for (const item of raw) {
        const parsed = intakeLinkSchema.safeParse(item);
        if (!parsed.success)
            continue;
        const url = normalizeUrl(parsed.data.url);
        if (!isSafeHttpUrl(url))
            continue;
        out.push({ label: parsed.data.label, url, ...(parsed.data.note ? { note: parsed.data.note } : {}) });
        if (out.length >= MAX_ONBOARDING_LINKS)
            break;
    }
    return out;
}
/**
 * Normalize a `link` field's URL on write: a bare host gains `https://`, and anything that is not
 * http(s) after normalization is cleared (blocks `javascript:`/`data:` in a form-authored link the
 * talent clicks). Non-link fields pass through untouched. Apply to every field when saving a form.
 */
export function cleanIntakeFieldLink(field) {
    if (field.type !== 'link')
        return field;
    const url = field.url ? normalizeUrl(field.url) : '';
    return { ...field, url: isSafeHttpUrl(url) ? url : '' };
}
/** Field ids that live on the client RECORD itself, not in the profile json. */
export const PROFILE_CORE_IDS = ['first_name', 'last_name', 'email', 'phone'];
/** Whether a field id is one of the core ids kept on the client row (PROFILE_CORE_IDS). */
export const isProfileCoreId = (id) => PROFILE_CORE_IDS.includes(id);
/**
 * The PROFILE fields proper: the schema minus the core ids. Name, email and phone live on the client row
 * and have their own inputs, so treating them as profile fields would show them empty, count them as
 * missing required items, and render them twice next to the core inputs. Used by the agent's client
 * pages and every portal profile surface alike.
 */
export const nonCoreProfileFields = (fields) => fields.filter((f) => !isProfileCoreId(f.id));
/**
 * Effective portal access — just two levels. Unset defaults to editable; the deprecated
 * 'internal' collapses to 'client_read' so the client can always SEE what they provided.
 */
export const fieldAccess = (f) => (f.access ?? 'client_edit') === 'client_edit' ? 'client_edit' : 'client_read';
/**
 * The fields a talent may edit on ONE agency's portal profile: profile fields (never a core id) the
 * agency marks client_edit. Sensitive ones included: they are stored encrypted on that agency's record.
 * The one home for "what the talent may edit"; portableEditableFields narrows it for cross-agency use.
 */
export const talentEditableFields = (fields) => nonCoreProfileFields(fields).filter((f) => fieldAccess(f) === 'client_edit');
/**
 * The talent-editable fields that may travel ACROSS agencies (the All-agency profile, its saves and the
 * "send updates to your agencies" push): never a sensitive one, which stays encrypted per agency.
 */
export const portableEditableFields = (fields) => talentEditableFields(fields).filter((f) => !f.sensitive);
/** Field types whose answers can live on a client profile (files stay media; info/link are decorative). */
export const isProfileFieldType = (t) => t !== 'file' && !isDisplayOnlyField(t);
/**
 * The business's profile schema, given its stored override (none, or no field of it readable: the default template).
 * Read field by field (storedFieldsOf): one field this build cannot read is left out, never the whole schema, so
 * every sensitive mark and every other field the agency set up keeps applying.
 */
export function profileFieldsOf(stored) {
    const { fields } = storedFieldsOf(stored);
    return fields.length ? fields : DEFAULT_PROFILE_FIELDS;
}
/**
 * Validate profile VALUES (a partial edit) against the schema: per-field type rules
 * apply but nothing is required — agents and intake may fill any subset.
 */
export function validateProfileValues(fields, values) {
    const lax = fields.filter((f) => isProfileFieldType(f.type)).map((f) => ({ ...f, required: false }));
    const r = validateIntakeAnswers(lax, values);
    return r.ok ? { ok: true, values: r.answers } : r;
}
/**
 * Starter template — modeled on a real agency's new-talent intake
 * (identity → vitals → contact → guardians → photo + signed paperwork → payment).
 */
export const DEFAULT_INTAKE_FIELDS = [
    { id: 'first_name', type: 'short_text', label: "Talent's first name", required: true },
    { id: 'last_name', type: 'short_text', label: "Talent's last name", required: true },
    // Birthday is fixed identity data the agency relies on — read-only to the client (it never changes).
    { id: 'dob', type: 'date', label: "Talent's date of birth", required: true, access: 'client_read' },
    { id: 'gender', type: 'select', label: "Talent's gender", help: 'Helps with casting and wardrobe planning.', options: ['Female', 'Male', 'Non-binary', 'Prefer to self-describe'] },
    // Vitals: the talent enters and keeps these current themselves — they change over time
    // (height/weight/hair/sizes), so they're client-EDITABLE by default. Listed as the canonical
    // casting attributes ([[PORTABLE_PROFILE_FIELDS]] mirrors this set on the All-agency profile).
    { id: 'height', type: 'short_text', label: 'Height', help: 'e.g. 5\'4" or 49"' },
    { id: 'weight', type: 'short_text', label: 'Weight', help: 'e.g. 62 lbs' },
    { id: 'hair', type: 'short_text', label: 'Hair color' },
    { id: 'eyes', type: 'short_text', label: 'Eye color' },
    { id: 'age', type: 'number', label: 'Age', help: 'Your current age (kept separate from your date of birth).' },
    { id: 'shirt_size', type: 'short_text', label: 'Shirt size', help: 'e.g. M, 15.5/34' },
    { id: 'pants_size', type: 'short_text', label: 'Pants size', help: 'e.g. 32x32' },
    { id: 'dress_size', type: 'short_text', label: 'Dress size', help: 'e.g. 6' },
    { id: 'shoe_size', type: 'short_text', label: 'Shoe size' },
    { id: 'ethnicity', type: 'long_text', label: 'Ethnicity', help: 'Please be as specific as possible; list multiple if applicable.' },
    { id: 'union_status', type: 'select', label: 'Union status', options: ['Non-union', 'SAG-AFTRA Eligible', 'SAG-AFTRA'] },
    { id: 'imdb_url', type: 'short_text', label: 'IMDb page', help: 'Link if you have one.' },
    { id: 'parents_email', type: 'email', label: "Parent/guardian's email (minors)", help: 'Where booking paperwork goes.' },
    {
        id: 'ssn',
        type: 'short_text',
        label: 'Social Security number',
        help: 'Needed for payroll/work permits. Stored encrypted; only your agency can access it.',
        access: 'client_read',
        sensitive: true,
    },
    { id: 'work_permit_expiry', type: 'date', label: 'Work permit expiration (minors)' },
    { id: 'passport_status', type: 'select', label: 'Passport', options: ['None', 'Pending', 'Valid'] },
    {
        id: 'divisions',
        type: 'matrix',
        label: 'Which categories are you submitting for?',
        help: 'Pick the level that fits each category.',
        rows: ['Film / TV', 'Commercial', 'Theater', 'Print', 'Voiceover', 'Dance / Movement'],
        columns: ['Interested', 'Some experience', 'Not for me'],
    },
    { id: 'email', type: 'email', label: 'Email address', help: 'Talent or parent/guardian.', required: true },
    { id: 'phone', type: 'phone', label: 'Contact number', help: 'Talent or parent/guardian — include country code.', required: true },
    { id: 'address', type: 'long_text', label: 'Home address', help: 'Include city, state, and zip code.', required: true },
    { id: 'guardians', type: 'long_text', label: 'Parents / guardians (minors only)', help: "Full name, relationship, phone, and email for each parent/guardian the talent lives with." },
    { id: 'photo', type: 'file', label: 'Recent photo', help: "Doesn't need to be professional — a well-lit snapshot is perfect.", accept: 'image', required: true },
    { id: 'paperwork', type: 'file', label: 'Signed paperwork', help: 'Completed and signed agency forms (PDF or image).', accept: 'document' },
    { id: 'payment_pref', type: 'select', label: 'Payment preference', options: ['Direct deposit', 'Check'], help: 'You can change this later.' },
];
/** Title of the prebuilt primary intake form auto-seeded for every agency. */
export const PRIMARY_FORM_TITLE = 'New Talent Intake';
/**
 * Agent-side bookkeeping the intake never asks for (lives only on the profile schema).
 * Casting-site logins are NOT captured here — the credential vault owns them now
 * (see credentials.ts / the "sites we ask for" step), so the profile stays login-free.
 */
export const AGENT_ONLY_PROFILE_FIELDS = [
    { id: 'contract_expiration', type: 'date', label: 'Contract expiration', access: 'client_read' },
    { id: 'commission_rate', type: 'number', label: 'Commission rate (%)', access: 'client_read' },
];
/** The default client-profile schema = the intake template's storable fields + agent bookkeeping. */
export const DEFAULT_PROFILE_FIELDS = [
    ...DEFAULT_INTAKE_FIELDS.filter((f) => isProfileFieldType(f.type)),
    ...AGENT_ONLY_PROFILE_FIELDS,
];
/**
 * The canonical portable attributes the talent sets ONCE on their All-agency profile and
 * pushes out to every agency. These always render on the cross-agency profile (even before any
 * agency has asked for them) so the talent can keep a single set of "defaults" current. The
 * server unions these (by id) with the editable fields drawn from the talent's agencies, so an
 * agency that customized the label/help for one of these still wins for its own per-agency view.
 */
export const PORTABLE_PROFILE_FIELD_IDS = [
    'height', 'weight', 'hair', 'eyes', 'age',
    'shirt_size', 'pants_size', 'dress_size', 'shoe_size',
    'gender', 'ethnicity', 'union_status', 'imdb_url',
];
export const PORTABLE_PROFILE_FIELDS = DEFAULT_INTAKE_FIELDS.filter((f) => PORTABLE_PROFILE_FIELD_IDS.includes(f.id) && isProfileFieldType(f.type)).map((f) => ({ ...f, access: 'client_edit' }));
//# sourceMappingURL=intake.js.map